/**
 * Règles de collecte : le formulaire (création et règle proposée après un
 * arbitrage), la liste avec ses actions, l'aperçu des pièces captées avant
 * d'enregistrer. Une règle n'est jamais supprimée : elle se désactive.
 */

import { dateFr, entier } from '@scc/core';
import { h, vider } from '../../dom.js';
import {
  creerRegleCollecte,
  modifierRegleCollecte,
  reglesCollecte,
  testerRegleCollecte,
  type NaturePiece,
  type RegleCollecte,
  type SaisieRegleCollecte,
  type StatutRegleCollecte,
} from './api.js';
import { brut, creerRetour, creerSection, messageDe, table, verrouille, type Contexte, type Section } from './communs.js';
import { criteresEnMots, effetEnMots, ETATS_PIECE, NATURES } from './pieces-textes.js';

type Controle = HTMLButtonElement | HTMLInputElement | HTMLSelectElement;

const STATUTS: Readonly<Record<StatutRegleCollecte, string>> = { proposee: 'Proposée', active: 'Active', inactive: 'Inactive' };

/** Natures qu'une règle peut donner (« à trancher » est ce qu'une règle ne tranche pas). */
const NATURES_DE_REGLE: readonly NaturePiece[] = ['facture_scc', 'autre_structure', 'facture_emise', 'pas_une_facture'];

export type ReglePreparee = Omit<SaisieRegleCollecte, 'statut'>;

export interface FormulaireRegle {
  readonly el: HTMLElement;
  readonly controles: readonly Controle[];
  /** La règle saisie, ou le texte de ce qui manque. */
  lire(): { regle: ReglePreparee } | { erreur: string };
  vider(): void;
}

const texteOuNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/**
 * Champs d'une règle. `simple` : seulement ce qu'on ajuste à chaud après un
 * arbitrage (expéditeur, sujet, nature, fournisseur, rubrique) ; les autres
 * critères et options gardent la valeur proposée.
 */
export function formulaireRegle(init: Partial<ReglePreparee>, simple: boolean): FormulaireRegle {
  const champ = (placeholder: string, valeur: string | null | undefined, max = 200): HTMLInputElement =>
    h('input', { type: 'text', maxlength: max, placeholder, value: valeur ?? '' });
  const expediteur = champ('edf.fr ou facture@edf.fr', init.expediteur);
  const boite = champ('gestion@compagnies.org', init.boite);
  const sujet = champ('facture', init.sujetContient);
  const fichier = champ('facture', init.fichierContient);
  const nature = h('select', {}, ...NATURES_DE_REGLE.map((n) => h('option', { value: n }, NATURES[n])));
  nature.value = init.nature ?? 'facture_scc';
  const fournisseur = champ('Nom du fournisseur, facultatif', init.fournisseur, 100);
  const rubrique = h('input', { type: 'text', inputmode: 'numeric', maxlength: 3, placeholder: 'ex. 11', value: init.rubrique ?? '', classe: 'champ-seuil' });
  const depotAuto = h('input', { type: 'checkbox' });
  depotAuto.checked = init.depotAuto ?? false;
  const jamaisJev = h('input', { type: 'checkbox' });
  jamaisJev.checked = init.jamaisJev ?? false;

  const etiquette = (libelle: string, c: Controle, aide?: string): HTMLElement =>
    h('label', {}, libelle, c, aide === undefined ? null : h('span', { classe: 'note-lecture' }, aide));

  const el = h(
    'div',
    {},
    h(
      'div',
      { classe: 'editeur-champs' },
      etiquette('L’expéditeur (adresse ou domaine)', expediteur),
      simple ? null : etiquette('La boîte d’origine', boite),
      etiquette('Le sujet contient', sujet),
      simple ? null : etiquette('Le nom du fichier contient', fichier),
      etiquette('Alors la pièce est', nature),
      etiquette('Fournisseur', fournisseur),
      etiquette('Rubrique du Drive', rubrique),
    ),
    simple
      ? null
      : h(
          'div',
          { classe: 'editeur-champs' },
          h('label', { classe: 'case' }, 'Déposer sans validation (factures reçues par le SCC, lues sans doute)', depotAuto),
          h('label', { classe: 'case' }, 'Ne jamais envoyer le texte de la pièce à Jev', jamaisJev),
        ),
  );
  const controles: Controle[] = [expediteur, boite, sujet, fichier, nature, fournisseur, rubrique, depotAuto, jamaisJev];

  return {
    el,
    controles,
    lire() {
      const regle: ReglePreparee = {
        expediteur: texteOuNull(expediteur.value),
        boite: simple ? (init.boite ?? null) : texteOuNull(boite.value),
        sujetContient: texteOuNull(sujet.value),
        fichierContient: simple ? (init.fichierContient ?? null) : texteOuNull(fichier.value),
        nature: nature.value as NaturePiece,
        fournisseur: texteOuNull(fournisseur.value),
        rubrique: texteOuNull(rubrique.value),
        depotAuto: simple ? (init.depotAuto ?? false) : depotAuto.checked,
        jamaisJev: simple ? (init.jamaisJev ?? false) : jamaisJev.checked,
      };
      if (criteresEnMots(regle) === 'aucun critère') return { erreur: 'Indiquez au moins un critère : une règle sans critère ne capte rien.' };
      if (regle.rubrique !== null && !/^\d{1,3}$/.test(regle.rubrique)) return { erreur: 'La rubrique est un numéro, par exemple 11.' };
      if (regle.depotAuto && regle.nature !== 'facture_scc') return { erreur: 'Le dépôt sans validation ne vaut que pour les factures reçues par le SCC.' };
      return { regle };
    },
    vider() {
      for (const c of [expediteur, boite, sujet, fichier, fournisseur, rubrique]) c.value = '';
      nature.value = 'facture_scc';
      depotAuto.checked = false;
      jamaisJev.checked = false;
    },
  };
}

/**
 * Règle tirée d'une pièce arbitrée : la personne l'accepte (elle devient active),
 * l'ajuste avant de l'accepter, ou la refuse (rien n'est enregistré).
 */
export function bandeauRegleProposee(proposee: SaisieRegleCollecte, ctx: Contexte, surFin: () => void): HTMLElement {
  const form = formulaireRegle(proposee, true);
  const retour = creerRetour();
  const accepter = h('button', { type: 'button', classe: 'btn-action principal' }, 'Créer la règle');
  const refuser = h('button', { type: 'button', classe: 'btn-action' }, 'Ne pas créer de règle');
  const controles = [...form.controles, accepter, refuser];

  accepter.addEventListener('click', () => {
    const l = form.lire();
    if ('erreur' in l) {
      retour.ko(l.erreur);
      return;
    }
    void (async () => {
      try {
        await verrouille(controles, () => creerRegleCollecte({ ...l.regle, statut: 'active' }));
        ctx.retour.ok('Règle de collecte créée : elle trie désormais les pièces qui lui correspondent.');
        ctx.recharger('regles');
        surFin();
      } catch (e) {
        retour.ko(messageDe(e, 'La règle n’a pas pu être créée.'));
      }
    })();
  });
  refuser.addEventListener('click', surFin);

  return h(
    'div',
    { classe: 'card bandeau-regle', role: 'group', 'aria-label': 'Règle proposée' },
    h('h3', {}, 'Règle proposée'),
    h(
      'p',
      {},
      'Cette décision peut servir de règle : les prochaines pièces qui y correspondent seront triées d’office. ',
      'Ajustez-la si besoin, puis créez-la ou laissez-la de côté.',
    ),
    form.el,
    h('div', { classe: 'barre-actions' }, accepter, refuser),
    retour.el,
  );
}

// ── Section ──────────────────────────────────────────────────────────────

export interface SectionRegles {
  readonly section: Section;
  charger(): Promise<void>;
}

export function sectionRegles(ctx: Contexte): SectionRegles {
  const section = creerSection(8, 'Règles de collecte', 'Ce qui trie les pièces sans qu’on ait à le refaire');
  const zoneListe = h('div', {});
  const retourListe = ctx.retour;

  const changer = (r: RegleCollecte, maj: Partial<SaisieRegleCollecte>, bouton: HTMLButtonElement, ok: string): void => {
    void (async () => {
      try {
        await verrouille([bouton], () => modifierRegleCollecte(r.id, maj));
        retourListe.ok(ok);
        await charger();
      } catch (e) {
        retourListe.ko(messageDe(e, 'La règle n’a pas pu être modifiée.'));
      }
    })();
  };

  const actions = (r: RegleCollecte): HTMLElement => {
    const bouton = (libelle: string, maj: Partial<SaisieRegleCollecte>, ok: string, avant?: () => boolean): HTMLButtonElement => {
      const b: HTMLButtonElement = h(
        'button',
        {
          type: 'button',
          classe: 'btn-action',
          surClick: () => {
            if (avant && !avant()) return;
            changer(r, maj, b, ok);
          },
        },
        libelle,
      );
      return b;
    };
    return h(
      'div',
      { classe: 'actions-arbitrage' },
      r.statut !== 'active' ? bouton(r.statut === 'proposee' ? 'Valider' : 'Réactiver', { statut: 'active' }, 'Règle validée : elle trie les prochaines pièces.') : null,
      r.statut !== 'inactive' ? bouton(r.statut === 'proposee' ? 'Écarter' : 'Désactiver', { statut: 'inactive' }, 'Règle désactivée.') : null,
      r.statut === 'active' && r.nature === 'facture_scc'
        ? r.depotAuto
          ? bouton('Exiger la validation', { depotAuto: false }, 'Cette règle ne dépose plus seule : ses pièces attendent une validation.')
          : bouton(
              'Autoriser le dépôt sans validation',
              { depotAuto: true },
              'Cette règle dépose seule les pièces lues sans doute.',
              () =>
                window.confirm(
                  'Les factures captées par cette règle, lues sans champ manquant ni doute, seront déposées dans le Drive sans validation. Autoriser ?',
                ),
            )
        : null,
    );
  };

  const ligne = (r: RegleCollecte): HTMLElement =>
    h(
      'tr',
      { classe: r.statut === 'inactive' ? 'regle-inactive' : null },
      h('td', {}, brut(criteresEnMots(r))),
      h('td', {}, brut(effetEnMots(r))),
      h('td', { classe: 'num' }, entier(r.captees)),
      h('td', {}, STATUTS[r.statut], h('div', { classe: 'note-lecture' }, brut(`${r.creePar}, ${dateFr(r.creeLe.slice(0, 10))}`))),
      h('td', {}, actions(r)),
    );

  const dessiner = (regles: readonly RegleCollecte[]): void => {
    vider(zoneListe);
    const proposees = regles.filter((r) => r.statut === 'proposee').length;
    if (proposees > 0) {
      const pl = proposees > 1;
      zoneListe.appendChild(
        h(
          'p',
          { classe: 'note-lecture' },
          h('strong', {}, `${entier(proposees)} règle${pl ? 's' : ''} proposée${pl ? 's' : ''}`),
          ` attend${pl ? 'ent' : ''} une décision : une règle proposée ne trie rien tant qu’elle n’est pas validée.`,
        ),
      );
    }
    zoneListe.appendChild(
      regles.length === 0
        ? h('p', { classe: 'note-lecture' }, 'Aucune règle de collecte.')
        : table('Règles de collecte', ['Quand', 'Alors', ['Pièces triées', 'num'], 'Statut', 'Actions'], regles.map(ligne)),
    );
  };

  const charger = async (): Promise<void> => {
    dessiner((await reglesCollecte()).regles);
  };

  // ── Création ──
  const form = formulaireRegle({}, false);
  const retourForm = creerRetour();
  const zoneApercu = h('div', {});
  const boutonTester = h('button', { type: 'button', classe: 'btn-action' }, 'Voir les pièces captées');
  const boutonEnregistrer = h('button', { type: 'button', classe: 'btn-action principal' }, 'Enregistrer la règle');
  const controles = [...form.controles, boutonTester, boutonEnregistrer];

  boutonTester.addEventListener('click', () => {
    const l = form.lire();
    if ('erreur' in l) {
      retourForm.ko(l.erreur);
      return;
    }
    void (async () => {
      try {
        const { regle } = l;
        const a = await verrouille(controles, () =>
          testerRegleCollecte({ expediteur: regle.expediteur, boite: regle.boite, sujetContient: regle.sujetContient, fichierContient: regle.fichierContient }),
        );
        retourForm.effacer();
        vider(zoneApercu);
        zoneApercu.appendChild(
          h(
            'div',
            { classe: 'card' },
            h('p', {}, h('strong', {}, `${entier(a.pieces)} pièce${a.pieces > 1 ? 's' : ''} déjà reçue${a.pieces > 1 ? 's' : ''} correspond${a.pieces > 1 ? 'ent' : ''} à ces critères`), a.pieces > 10 ? ' — les 10 premières :' : '.'),
            a.exemples.length > 0
              ? h(
                  'ul',
                  { classe: 'exemples-regle' },
                  ...a.exemples.map((x) =>
                    h('li', {}, brut(`${dateFr(x.date.slice(0, 10))} — ${x.expediteur} — « ${x.sujet} » — ${x.nomOrigine} (${ETATS_PIECE[x.etat].toLowerCase()})`)),
                  ),
                )
              : null,
          ),
        );
      } catch (e) {
        retourForm.ko(messageDe(e, 'L’aperçu n’a pas pu être calculé.'));
      }
    })();
  });

  boutonEnregistrer.addEventListener('click', () => {
    const l = form.lire();
    if ('erreur' in l) {
      retourForm.ko(l.erreur);
      return;
    }
    void (async () => {
      try {
        await verrouille(controles, () => creerRegleCollecte({ ...l.regle, statut: 'active' }));
        retourForm.ok('Règle enregistrée et active : elle trie les prochaines pièces. Les pièces déjà reçues ne sont pas retriées.');
        form.vider();
        vider(zoneApercu);
        await charger();
      } catch (e) {
        retourForm.ko(messageDe(e, 'La règle n’a pas pu être enregistrée.'));
      }
    })();
  });

  section.corps.append(
    h(
      'p',
      { classe: 'note-lecture' },
      'Quand tous les critères renseignés correspondent à une pièce, la règle lui donne sa nature, et pour une facture reçue par le SCC son fournisseur et sa rubrique. ',
      'Une règle n’est jamais supprimée : on la désactive, et chaque changement est journalisé.',
    ),
    zoneListe,
    h(
      'div',
      { classe: 'card' },
      h('h3', {}, 'Nouvelle règle'),
      form.el,
      h('div', { classe: 'barre-actions' }, boutonTester, boutonEnregistrer),
      retourForm.el,
      zoneApercu,
    ),
  );

  return { section, charger };
}
